import { HourFormat } from '../types/brand';
import {
  SailingDay,
  SailingDayType,
  BareSailing,
  Sailing,
  BareSailingDay,
  SailingDayLocation,
  SimpleSailing,
  BareSimpleSailing,
  BareSimpleSailingDay,
  SimpleSailingDayBundle,
  SailingDayBundle,
  BareSailingDayBundle,
  BareSimpleSailingDayBundle,
  SimpleSailingDay,
} from '../types/sailing';
import { DayType } from '../types/venueTemplate';
import { Time } from '../types/time';
import { groupBy } from './functional';
import { convertToSimpleTemplate } from './template';

export function getBookingDayType(
  dayIndex: number,
  totalDays: number,
  type: SailingDayType
): DayType {
  if (dayIndex === 0) return 'embark';
  if (dayIndex === totalDays - 1) return 'debark';
  return type;
}
import { timeToString, isoDate, getLocaleUTCDateString, TimeStringFormat } from './time';
import { safeIdCompare } from './utils';

/**
 * A Sailing day location is its location if it exists. If it doesn't, sea days
 * should return "At Sea" and port days an empty string.
 *
 */
interface DayWithLocations {
  type: SailingDayType;
  locations: SailingDayLocation[];
}

export function getDayLocation(day: DayWithLocations): string {
  if (day.locations.length > 0) return day.locations.map(l => l.name).join('/');
  return day.type === 'sea' ? 'At Sea' : '';
}

/**
 * validate a SailingDayType
 *
 */
export function isValidSailingDayType(u: unknown): u is SailingDayType {
  return u === 'sea' || u === 'port';
}

/**
 * Returns the first port of the first day of the sailing, or an empty string there isn't one
 *
 */
export function getSailingInitialPort(
  sailing: Sailing | BareSailing | SimpleSailing | BareSimpleSailing
): string {
  if (sailing.days[0] && sailing.days[0].locations[0]) {
    return sailing.days[0].locations[0].name;
  }
  return '';
}

/**
 * Returns the first arrival of a sailing day as a string, or ''
 */
export function getDayFirstArrival(
  day: DayWithLocations,
  hourFormat: HourFormat,
  timeFormat: TimeStringFormat
): string {
  let firstArrival: Time | undefined;
  for (let location of day.locations) {
    if (location.arrival) {
      firstArrival = location.arrival;
      break;
    }
  }
  return firstArrival ? timeToString(firstArrival, hourFormat, timeFormat) : '';
}

/**
 * Returns the last departure of a sailing day as a string, or ''
 */
export function getDayLastDeparture(
  day: DayWithLocations,
  hourFormat: HourFormat,
  timeFormat: TimeStringFormat
): string {
  let lastDeparture: Time | undefined;
  for (let location of day.locations) {
    if (location.departure) {
      lastDeparture = location.departure;
    }
  }
  return lastDeparture ? timeToString(lastDeparture, hourFormat, timeFormat) : '';
}

export function getDayFriendlyName(
  sailing: Sailing | BareSailing | BareSimpleSailing,
  day: SailingDay | BareSailingDay | BareSimpleSailingDay
) {
  return `${getDayLocation(day)} - ${sailing.name} (Day ${day.name}, ${getLocaleUTCDateString(
    day.date
  )})`;
}

export function convertToSimpleSailingDay(sailingDay: SailingDay): SimpleSailingDay {
  const { feeds: _, ...rest } = sailingDay;
  return {
    ...rest,
    template: sailingDay.template ? convertToSimpleTemplate(sailingDay.template) : undefined,
  };
}

export function convertToSimpleSailing(sailing: Sailing): SimpleSailing {
  return {
    ...sailing,
    days: sailing.days.map(convertToSimpleSailingDay),
    template: sailing.template
      ? {
          ...sailing.template,
          dayTemplates: sailing.template.dayTemplates.map(convertToSimpleTemplate),
        }
      : undefined,
  };
}

export function convertToBareSimpleSailingDay(sailingDay: BareSailingDay): BareSimpleSailingDay {
  const { feeds: _, ...rest } = sailingDay;
  return rest;
}

export function convertToBareSimpleSailing(sailing: BareSailing): BareSimpleSailing {
  return {
    ...sailing,
    days: sailing.days.map(convertToBareSimpleSailingDay),
  };
}

export function findSailingBundleByDateAndDay(
  date: Date,
  dayIndex: number,
  sailings: Sailing[]
): SailingDayBundle | undefined;
export function findSailingBundleByDateAndDay(
  date: Date,
  dayIndex: number,
  sailings: BareSailing[]
): BareSailingDayBundle | undefined;
export function findSailingBundleByDateAndDay(
  date: Date,
  dayIndex: number,
  sailings: SimpleSailing[]
): SimpleSailingDayBundle | undefined;
export function findSailingBundleByDateAndDay(
  date: Date,
  dayIndex: number,
  sailings: BareSimpleSailing[]
): BareSimpleSailingDayBundle | undefined;
export function findSailingBundleByDateAndDay(
  date: Date,
  dayIndex: number,
  sailings: Sailing[] | BareSailing[] | SimpleSailing[] | BareSimpleSailing[]
) {
  const sailingDayMap = groupBy(
    sailings
      .map(s =>
        s.days.map((d, i) => ({ dateIso: isoDate(d.date), dayIndex: i, sailing: s, day: d }))
      )
      .flat(),
    'dateIso'
  );

  const allMatches = sailingDayMap[isoDate(date)];

  const sailingDayInfo = (allMatches || []).find(m => m.dayIndex === dayIndex);

  return sailingDayInfo;
}

export function makeSailingDayBundle(
  sailing: BareSailing,
  day: BareSailingDay
): BareSailingDayBundle;
export function makeSailingDayBundle(sailing: Sailing, day: SailingDay): SailingDayBundle;
export function makeSailingDayBundle(
  sailing: BareSailing | Sailing,
  day: BareSailingDay | SailingDay
) {
  return {
    dateIso: isoDate(day.date),
    dayIndex: sailing.days.findIndex(d => safeIdCompare(d._id, day._id)),
    sailing,
    day,
  };
}

export function convertToSimpleSailingDayBundle(bundle: SailingDayBundle): SimpleSailingDayBundle {
  return {
    dateIso: bundle.dateIso,
    dayIndex: bundle.dayIndex,
    sailing: convertToSimpleSailing(bundle.sailing),
    day: convertToSimpleSailingDay(bundle.day),
  };
}
